import i18next from 'i18next';
import sanitizeHtml from 'sanitize-html';
import { DeepReadonly } from 'utility-types';
import { IQuestionnaireSection } from '@pec/aion-ui-core/interfaces/questionnaireSection';
import { ISectionNav } from 'interfaces/sections';
import { ISectionStatus } from '@pec/aion-ui-core/interfaces/questionnaireStatus';
import { ISegmentStatus } from 'interfaces/segmentStatuses';
import { Theme } from '@material-ui/core/styles';
import { xor } from 'lodash';

export const getPredictiveRankingScoreString = (score: number) =>
  ({
    1: i18next.t('subscriber:rankingScore.standardRisk', 'Standard Risk'),
    2: i18next.t('subscriber:rankingScore.elevatedRisk', 'Elevated Risk'),
    3: i18next.t('subscriber:rankingScore.highRisk', 'High Risk')
  }[score] || i18next.t('subscriber:rankingScore.standardRisk', 'Standard Risk'));

export const getPredictiveRankingScoreColor = (theme: Theme, score: number) =>
  ({
    1: theme.palette.secondary.main,
    2: theme.palette.error.light,
    3: theme.palette.error.main
  }[score] || theme.palette.secondary.main);

export const filterQuestionnaireSections = (
  sections: DeepReadonly<IQuestionnaireSection[]> | null,
  isEnabled?: boolean
) => {
  const sectionIDsToCheck = [1, 2, 3, 224];

  return isEnabled ? sections?.filter(section => !sectionIDsToCheck.includes(section.id)) : sections;
};

export const isOrganizationProfileLoaded = (organizationProfile: object) =>
  Object.keys(organizationProfile)
    .map(key => Object.values(organizationProfile[key]).every((status: boolean | null) => status !== null))
    .some(value => value);

export const renderHTMLString = (string: string) =>
  sanitizeHtml(string, {
    allowedTags: sanitizeHtml.defaults.allowedTags,
    transformTags: {
      a: (tagName: string, attribs: object) => ({
        tagName,
        attribs: {
          ...attribs,
          target: '_blank'
        }
      })
    }
  });

export type QuestionnaireProgressResult = {
  answeredQuestionsCount: number;
  totalQuestionsCount: number;
  answered: ISectionStatus[];
  inProgress: ISectionStatus[];
  notAnswered: ISectionStatus[];
};

export const getStatusSegmentSections = (
  sections: ISectionNav[],
  segmentStatuses: ISegmentStatus[],
  activeSubscriptionId: string,
  hasSubscriptions: boolean
) => {
  // Map for status ordering (from segmentStatuses)
  const statusPriorityMap = new Map<string, number>();
  segmentStatuses.forEach(status => {
    if (!statusPriorityMap.has(status.contractorStatus)) {
      statusPriorityMap.set(status.contractorStatus, status.priority);
    }
  });

  const grouped = sections
    .map(section => {
      const matching = segmentStatuses.find(s => s.segmentId === section.moduleId);
      return matching
        ? {
            ...section,
            contractorStatus: matching.contractorStatus,
            priority: matching.priority,
            segmentId: matching.segmentId,
            subscriptionId: matching.subscriptionId,
            legacySubscriptionTypeId: matching.legacySubscriptionTypeId?.toString() ?? ''
          }
        : section;
    })
    .filter(section => {
      if (hasSubscriptions) {
        return activeSubscriptionId === 'null'
          ? !section.subscriptionId
          : section.legacySubscriptionTypeId === activeSubscriptionId;
      }
      return true;
    })
    .sort((a, b) => {
      if (isFinite(Number(a.priority)) && isFinite(Number(a.priority))) {
        return Number(a.priority) - Number(b.priority);
      }
      return -1;
    })
    .reduce((acc: Record<string, Record<string, ISectionNav[]>>, section) => {
      const statusKey = section?.contractorStatus || 'null';
      const moduleKey = section.moduleId || 'null';

      if (acc[statusKey]) {
        if (acc[statusKey][moduleKey]) {
          acc[statusKey][moduleKey].push(section);
        } else {
          acc[statusKey][moduleKey] = [section];
        }
      } else {
        acc[statusKey] = {
          [moduleKey]: [section]
        };
      }
      return acc;
    }, {});

  // Sort status groups by priority
  const sortedEntries = Object.entries(grouped).sort(([statusA], [statusB]) => {
    if (statusA === 'null') return 1;
    if (statusB === 'null') return -1;

    const priorityA = statusPriorityMap.get(statusA) ?? Number.MAX_SAFE_INTEGER;
    const priorityB = statusPriorityMap.get(statusB) ?? Number.MAX_SAFE_INTEGER;

    return priorityA - priorityB;
  });

  return Object.fromEntries(sortedEntries);
};

export const arraysDifference = (prev: { id: string }[], next: { id: string }[]) => {
  const currentIds = prev.map(item => item.id);
  const loadedIds = next.map(item => item.id);
  const difference = xor(currentIds, loadedIds);
  const deleted = currentIds.filter(itemId => difference.includes(itemId));
  const added = loadedIds.filter(itemId => difference.includes(itemId));

  return { deleted, added };
};

export const isUUID = (value: string) => /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/.test(value);

export const getFileNameFromContentDisposition = (value: string) => {
  const result = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(value);

  if (result && result[1]) {
    return {
      fileName: result[1],
      extension: result[1].split('.').pop()
    };
  }

  return {
    fileName: '',
    extension: 'pdf'
  };
};
